import { Auth } from '@angular/fire/auth';
import Debug from 'debug';
import * as moment from 'moment';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
const debug = Debug('aumico:JwtUtils');

export interface SessionStatus {
    isExpiringSoon: boolean;
    hoursUntilExpiry: number;
    expiresAt: Date;
    auth_time: number;
}

export interface JwtClaims {
    auth_time: number;
    exp: number;
    iat: number;
    [key: string]: any;
}

export interface TokenWithClaims {
    token: string;
    claims: JwtClaims;
}

export class JwtUtils {
    private static readonly SESSION_DURATION_HOURS = 20;
    private static readonly SESSION_DURATION_MS = JwtUtils.SESSION_DURATION_HOURS * 60 * 60 * 1000; // 20 hours in milliseconds

    /**
     * Get the current user's ID token with claims
     */
    static getIdTokenWithClaims$(auth: Auth): Observable<TokenWithClaims> {
        if (!auth.currentUser) {
            throw new Error('No authenticated user');
        }

        return from(auth.currentUser.getIdTokenResult()).pipe(
            map((result) => ({
                token: result.token,
                claims: {
                    auth_time: Number(result.claims['auth_time']),
                    exp: Number(result.claims['exp']),
                    iat: Number(result.claims['iat']),
                    ...result.claims,
                } as JwtClaims,
            })),
        );
    }

    /**
     * Check if the session should expire soon based on auth_time and 20 hour limit
     */
    static checkSessionExpiration(claims: JwtClaims, warningThresholdHours: number = 2): SessionStatus {
        debug('[JwtUtils] Checking session expiration for claims:', claims);

        // Get the authentication time (when user first logged in)
        const authTime = claims.auth_time;
        const authTimeMs = authTime * 1000;

        debug('[JwtUtils] Auth time:', authTime, 'Date:', new Date(authTimeMs).toISOString());

        // Calculate when the session should expire (20 hours from auth_time)
        const sessionExpiresAt = new Date(authTimeMs + JwtUtils.SESSION_DURATION_MS);
        debug('[JwtUtils] Session expires at:', sessionExpiresAt.toISOString());

        // Calculate time until expiry
        const now = new Date();
        const msUntilExpiry = sessionExpiresAt.getTime() - now.getTime();
        const hoursUntilExpiry = msUntilExpiry / (1000 * 60 * 60);

        debug('[JwtUtils] Hours until expiry:', hoursUntilExpiry);
        debug('[JwtUtils] Warning threshold:', warningThresholdHours, 'hours');

        // Check if we should show warning
        const isExpiringSoon = hoursUntilExpiry <= warningThresholdHours && hoursUntilExpiry > 0;

        debug('[JwtUtils] Is expiring soon:', isExpiringSoon);

        return {
            isExpiringSoon,
            hoursUntilExpiry,
            expiresAt: sessionExpiresAt,
            auth_time: authTime,
        };
    }

    /**
     * Check if the session is already expired
     */
    static isSessionExpired(claims: JwtClaims): boolean {
        const authTime = claims.auth_time;
        const authTimeMs = authTime * 1000;
        const sessionExpiresAt = new Date(authTimeMs + JwtUtils.SESSION_DURATION_MS);
        const now = new Date();

        return now > sessionExpiresAt;
    }

    /**
     * Format time until expiry in a human-readable format
     * @param hoursUntilExpiry - Hours until expiry
     * @param locale - Optional locale string (e.g., 'en', 'de', 'fr'). If not provided, uses current moment locale
     */
    static formatTimeUntilExpiry(hoursUntilExpiry: number, locale?: string): string {
        // Set locale if provided
        const previousLocale = moment.locale();
        if (locale) {
            moment.locale(locale);
        }

        if (hoursUntilExpiry <= 0) {
            if (locale) moment.locale(previousLocale); // Restore previous locale
            return 'expired'; // Consider translating this too
        }

        const duration = moment.duration(hoursUntilExpiry, 'hours');

        let result: string;

        // For durations less than 24 hours, use simple humanize
        if (hoursUntilExpiry < 24) {
            result = duration.humanize();
        } else {
            // For longer durations, show days and hours if applicable
            const days = Math.floor(duration.asDays());
            const remainingHours = duration.hours();

            if (remainingHours === 0) {
                result = moment.duration(days, 'days').humanize();
            } else {
                // Humanize both parts separately for proper localization
                const daysText = moment.duration(days, 'days').humanize();
                const hoursText = moment.duration(remainingHours, 'hours').humanize();
                result = `${daysText} ${hoursText}`;
            }
        }

        // Restore previous locale
        if (locale) {
            moment.locale(previousLocale);
        }

        return result;
    }
}
